import { create } from 'zustand'
import { draftsDb } from '@/services/draftsDb'
import { meetingsApi, audioApi } from '@/services/api'
import { apiErrorMessage } from '@/utils/apiError'

/**
 * Pending audio-upload drafts. Audio blobs live in IndexedDB so they survive
 * tab reloads; metadata lives both in the store (for fast UI) and in IndexedDB
 * (for hydration on page load).
 */
export const useDraftsStore = create((set, get) => ({
  drafts: [],         // array of meta records {id, title, platform, meetingId?, filename, size, createdAt, lastError?}
  loaded: false,

  init: async () => {
    if (get().loaded) return
    try {
      const metas = await draftsDb.listMeta()
      set({ drafts: metas, loaded: true })
    } catch {
      set({ loaded: true })
    }
  },

  /**
   * Persist a blob + metadata as a new draft, returning the draft meta so the
   * caller can thread the id through retry/remove flows without re-listing.
   */
  create: async ({ title, platform, file }) => {
    const id = `draft-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
    const meta = {
      id,
      title,
      platform,
      filename: file.name,
      size: file.size,
      mimeType: file.type,
      createdAt: new Date().toISOString(),
    }
    await draftsDb.putBlob(id, file)
    await draftsDb.putMeta(meta)
    set(s => ({ drafts: [meta, ...s.drafts.filter(d => d.id !== id)] }))
    return meta
  },

  update: async (id, patch) => {
    const current = get().drafts.find(d => d.id === id) || (await draftsDb.getMeta(id))
    if (!current) return
    const next = { ...current, ...patch }
    await draftsDb.putMeta(next)
    set(s => ({ drafts: s.drafts.map(d => d.id === id ? next : d) }))
    return next
  },

  remove: async (id) => {
    await draftsDb.remove(id)
    set(s => ({ drafts: s.drafts.filter(d => d.id !== id) }))
  },

  /**
   * Pushes a draft through to a completed upload. Creates the meeting if we
   * haven't yet, then uploads the stored blob. On success the draft is
   * removed; on failure the error is stashed on the meta for display.
   */
  retry: async (id) => {
    const meta = get().drafts.find(d => d.id === id) || (await draftsDb.getMeta(id))
    if (!meta) throw new Error('Draft not found')
    const blob = await draftsDb.getBlob(id)
    if (!blob) throw new Error('Audio file no longer available')

    let meetingId = meta.meetingId
    try {
      if (!meetingId) {
        const res = await meetingsApi.create({
          title: meta.title,
          platform: meta.platform || 'upload',
        })
        meetingId = res.data.meeting?.id ?? res.data.id
        await get().update(id, { meetingId, lastError: null })
      }
      const file = new File([blob], meta.filename || 'audio.m4a', {
        type: meta.mimeType || blob.type || 'audio/mpeg',
      })
      await audioApi.upload(meetingId, file)
      await get().remove(id)
      return meetingId
    } catch (err) {
      // Stash the specific 422 field error on the draft so the
      // Pending Uploads card shows the actual reason (e.g. 'Session
      // title must include at least one letter…', 'The audio must be
      // a file of type: mp3, mp4, wav.') instead of Laravel's generic
      // 'Validation failed.' — QA's Retry-still-shows-generic bug.
      const msg = apiErrorMessage(err, 'Upload failed')
      await get().update(id, { lastError: msg, meetingId })
      throw err
    }
  },
}))
